/**
 * VoiceCall — client voice conversation with the AI assistant.
 *
 * Layout (single column, everything visible without scrolling):
 *   ┌──── compact header (status pill) ─────────────────────┐
 *   │ scrollable conversation thread (flex: 1)               │
 *   │ type-to-send compose bar                               │
 *   │ recent 5 conversations (compact chips + "More" link)   │
 *   │ Speak / End controls                                   │
 *   └────────────────────────────────────────────────────────┘
 *
 * Cross-session memory: on mount we adopt the most recent conversation's ID (not
 * its turns) and let the SERVER rehydrate the history from it. Replaying whole
 * transcripts from the browser is what used to make the assistant repeat itself
 * and drag in stale topics; the server condenses anything older than the last few
 * exchanges into a recap instead. Memory therefore no longer depends on React
 * state surviving a reload.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import {
  getConversation,
  listConversations,
  voiceTurn,
  type ConversationSummary,
  type VoiceHistoryMessage,
  VoiceApiError,
} from "./voiceApi";
import "./VoiceCall.css";

type Speaker = "agent" | "caller" | "system";

interface TranscriptTurn {
  id: string;
  speaker: Speaker;
  text: string;
}

type CallStatus = "idle" | "listening" | "thinking" | "speaking";

const STATUS_LABEL: Record<CallStatus, string> = {
  idle: "Ready",
  listening: "Listening…",
  thinking: "Thinking…",
  speaking: "Speaking…",
};

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult:
    | ((event: {
        results: ArrayLike<
          ArrayLike<{ transcript: string }> & { isFinal: boolean }
        >;
      }) => void)
    | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

function speechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

let turnCounter = 0;
const nextId = () => `t${++turnCounter}`;

export function VoiceCall() {
  const navigate = useNavigate();
  const [turns, setTurns] = useState<TranscriptTurn[]>([]);
  const [status, setStatus] = useState<CallStatus>("idle");
  const [draft, setDraft] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [voiceAvailable, setVoiceAvailable] = useState(true);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [showAllConvos, setShowAllConvos] = useState(false);

  const historyRef = useRef<VoiceHistoryMessage[]>([]);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const busyRef = useRef(false);
  const conversationIdRef = useRef<string | null>(null);

  const micSupported = speechRecognitionCtor() !== null;

  // On mount: load recent conversations. Auto-seed history from the most
  // recent one so the agent has cross-session memory without any click.
  useEffect(() => {
    const controller = new AbortController();
    listConversations(controller.signal)
      .then(async (convos) => {
        setConversations(convos);
        // Adopt the most recent conversation's ID so the next turn continues it.
        // We deliberately do NOT replay its turns into historyRef: verbatim replay
        // is what made the assistant repeat itself. The server loads the history
        // from this ID and condenses the older part of it (see load_history).
        const latest = convos[0];
        if (latest && !conversationIdRef.current) {
          conversationIdRef.current = latest.id;
          setActiveConversationId(latest.id);
        }
        setTurns([
          {
            id: nextId(),
            speaker: "system",
            text:
              convos.length > 0
                ? "You're connected to your brokerage's AI assistant. Tap the mic and speak, or type below. Tap a recent conversation below to continue where you left off."
                : "You're connected to your brokerage's AI assistant. Tap the mic and speak, or type below.",
          },
        ]);
      })
      .catch(() => {
        setTurns([
          {
            id: nextId(),
            speaker: "system",
            text: "You're connected to your brokerage's AI assistant. Tap the mic and speak, or type below.",
          },
        ]);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
  }, [turns, interim]);

  useEffect(
    () => () => {
      recognitionRef.current?.abort();
      audioRef.current?.pause();
    },
    [],
  );

  async function sendTurn(rawText: string) {
    const text = rawText.trim();
    if (!text || busyRef.current) return;
    busyRef.current = true;
    setError(null);
    setDraft("");
    setInterim("");
    setTurns((prev) => [...prev, { id: nextId(), speaker: "caller", text }]);
    setStatus("thinking");
    try {
      const result = await voiceTurn(
        text,
        historyRef.current,
        conversationIdRef.current,
      );
      historyRef.current = result.history;
      if (result.conversationId && !conversationIdRef.current) {
        conversationIdRef.current = result.conversationId;
        setActiveConversationId(result.conversationId);
        listConversations()
          .then(setConversations)
          .catch(() => undefined);
      }
      setTurns((prev) => [
        ...prev,
        { id: nextId(), speaker: "agent", text: result.text },
      ]);
      if (result.audioUrl && audioRef.current) {
        setStatus("speaking");
        const audio = audioRef.current;
        audio.src = result.audioUrl;
        audio.onended = () => {
          URL.revokeObjectURL(result.audioUrl as string);
          setStatus("idle");
        };
        try {
          await audio.play();
        } catch {
          setStatus("idle");
        }
      } else {
        if (!result.audioUrl) setVoiceAvailable(false);
        setStatus("idle");
      }
    } catch (err) {
      setStatus("idle");
      setError(
        err instanceof VoiceApiError && err.status === 401
          ? "Your session has expired — please sign in again."
          : "The assistant couldn't answer just now. Please try again.",
      );
    } finally {
      busyRef.current = false;
    }
  }

  function startListening() {
    const Ctor = speechRecognitionCtor();
    if (!Ctor || busyRef.current) return;
    const audio = audioRef.current;
    if (audio && !audio.paused) audio.pause();
    const recognition = new Ctor();
    recognitionRef.current = recognition;
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;
    let finalText = "";
    recognition.onresult = (event) => {
      let interimText = "";
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript ?? "";
        if (result?.isFinal) finalText += transcript;
        else interimText += transcript;
      }
      setInterim(interimText || finalText);
    };
    recognition.onerror = (event) => {
      setStatus("idle");
      setInterim("");
      if (event.error === "not-allowed") {
        setError(
          "Microphone access is blocked — allow it in your browser, or type instead.",
        );
      }
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setInterim("");
      if (finalText.trim()) void sendTurn(finalText);
      else setStatus((s) => (s === "listening" ? "idle" : s));
    };
    setError(null);
    setStatus("listening");
    recognition.start();
  }

  function stopListening() {
    recognitionRef.current?.stop();
  }

  async function resumeConversation(id: string) {
    if (busyRef.current || id === conversationIdRef.current) return;
    recognitionRef.current?.abort();
    audioRef.current?.pause();
    setError(null);
    try {
      const detail = await getConversation(id);
      conversationIdRef.current = id;
      setActiveConversationId(id);
      historyRef.current = detail.turns.map((t) => ({
        role: t.role,
        content: [{ type: "text", text: t.text }],
      }));
      setTurns([
        {
          id: nextId(),
          speaker: "system",
          text: "Continuing a previous conversation — I remember everything we discussed.",
        },
        ...detail.turns.map((t) => ({
          id: nextId(),
          speaker: (t.role === "assistant" ? "agent" : "caller") as Speaker,
          text: t.text,
        })),
      ]);
      setStatus("idle");
      setShowAllConvos(false);
    } catch {
      setError("Couldn't load that conversation. Please try again.");
    }
  }

  function startNewConversation() {
    if (busyRef.current) return;
    conversationIdRef.current = null;
    setActiveConversationId(null);
    historyRef.current = [];
    setTurns([
      {
        id: nextId(),
        speaker: "system",
        text: "New conversation started. Ask me anything.",
      },
    ]);
    setShowAllConvos(false);
  }

  const listening = status === "listening";
  // Show the 5 most recent in the bottom dock; the rest behind "More"
  const recentConvos = conversations.slice(0, 5);
  const hasMore = conversations.length > 5;

  return (
    <div className="vc-page">
      {/* ── Header ─────────────────────────────────────── */}
      <header className="vc-head">
        <div className="vc-head__left">
          <h1>AI assistant</h1>
          <p>
            {voiceAvailable
              ? "Voice replies on · answers also shown as text"
              : "Text answers only"}
          </p>
        </div>
        <div className={`vc-live vc-live--${status}`}>
          <span className="vc-live__dot" />
          {STATUS_LABEL[status]}
        </div>
      </header>

      {/* ── Wave bar when active ───────────────────────── */}
      {(listening || status === "speaking") && (
        <div className="vc-wave" aria-hidden="true">
          {Array.from({ length: 24 }, (_, i) => (
            <span
              key={i}
              style={{
                animationDelay: `${(i % 7) * 0.09}s`,
                animationDuration: `${0.7 + (i % 5) * 0.13}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* ── Conversation thread ────────────────────────── */}
      <div className="vc-thread" ref={threadRef}>
        {turns.map((turn) =>
          turn.speaker === "system" ? (
            <div key={turn.id} className="vc-sys">
              {turn.text}
            </div>
          ) : (
            <div key={turn.id} className={`vc-turn vc-turn--${turn.speaker}`}>
              <div className="vc-turn__av">
                {turn.speaker === "agent" ? "AI" : "You"}
              </div>
              <div className="vc-bubble">
                <div className="vc-bubble__meta">
                  {turn.speaker === "agent" ? "AI assistant" : "You"}
                </div>
                <div className="vc-bubble__text">{turn.text}</div>
              </div>
            </div>
          ),
        )}
        {interim && (
          <div className="vc-speaking">
            <span className="vc-live__dot" /> "{interim}"
          </div>
        )}
        {status === "thinking" && (
          <div className="vc-speaking">
            <span className="vc-live__dot" /> Assistant is thinking…
          </div>
        )}
      </div>

      {error && <div className="vc-error">{error}</div>}

      {/* ── Type-to-send compose bar ───────────────────── */}
      <form
        className="vc-compose"
        onSubmit={(event) => {
          event.preventDefault();
          void sendTurn(draft);
        }}
      >
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={
            micSupported
              ? "Type a question, or use the mic below"
              : "Type a question (voice input isn't supported in this browser)"
          }
          aria-label="Message the assistant"
          disabled={status === "thinking"}
        />
        <button type="submit" disabled={!draft.trim() || status === "thinking"}>
          Send
        </button>
      </form>

      {/* ── Recent conversations dock ──────────────────── */}
      {conversations.length > 0 && (
        <div className="vc-recent">
          <div className="vc-recent__row">
            <span className="vc-recent__label">Recent</span>
            {recentConvos.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`vc-chip ${c.id === activeConversationId ? "vc-chip--active" : ""}`}
                onClick={() => void resumeConversation(c.id)}
                title={`${c.title || "Conversation"} · ${new Date(c.last_activity_at).toLocaleDateString()}`}
              >
                {c.title || new Date(c.last_activity_at).toLocaleDateString()}
              </button>
            ))}
            {hasMore && (
              <button
                type="button"
                className="vc-chip vc-chip--more"
                onClick={() => setShowAllConvos((v) => !v)}
              >
                {showAllConvos ? "Less ▲" : `+${conversations.length - 5} more`}
              </button>
            )}
            {activeConversationId && (
              <button
                type="button"
                className="vc-chip vc-chip--new"
                onClick={startNewConversation}
              >
                + New
              </button>
            )}
          </div>

          {showAllConvos && (
            <div className="vc-all-convos">
              {conversations.slice(5).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`vc-convo-row ${c.id === activeConversationId ? "vc-convo-row--active" : ""}`}
                  onClick={() => void resumeConversation(c.id)}
                >
                  <span className="vc-convo-row__title">
                    {c.title || "Conversation"}
                  </span>
                  <span className="vc-convo-row__meta">
                    {new Date(c.last_activity_at).toLocaleDateString()} ·{" "}
                    {c.turn_count} messages
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Speak / End controls ───────────────────────── */}
      <div className="vc-controls">
        <button
          type="button"
          className={`vc-ctrl ${listening ? "vc-ctrl--active" : ""}`}
          onClick={listening ? stopListening : startListening}
          disabled={!micSupported || status === "thinking"}
          title={micSupported ? undefined : "Speech input not supported here"}
        >
          <span className="vc-ctrl__btn">🎙</span>
          <span className="vc-ctrl__label">{listening ? "Stop" : "Speak"}</span>
        </button>
        <button
          type="button"
          className="vc-ctrl vc-ctrl--end"
          onClick={() => {
            recognitionRef.current?.abort();
            audioRef.current?.pause();
            navigate("/");
          }}
        >
          <span className="vc-ctrl__btn">☎</span>
          <span className="vc-ctrl__label">End</span>
        </button>
      </div>

      <audio ref={audioRef} style={{ display: "none" }} />
    </div>
  );
}
